import { useMemo, useState } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Eye, Layers, PanelRight, Pencil, Plus, Unlink } from "lucide-react";

import { ConfirmationDialog } from "@/components/confirmation-dialog";
import type { SourcePdfRef } from "@/components/spec-pilot-source-pdf-panel";
import { SpecPilotSourcePdfPanel } from "@/components/spec-pilot-source-pdf-panel";
import { TableEditorModal } from "@/components/table-editor-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Toggle } from "@/components/ui/toggle";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";
import { cn } from "@/lib/utils";

import { buildRowLayout, excludedMemberTableIds, type GroupMember } from "./merge-layout";
import { MergeRowsDialog } from "./merge-rows-dialog";
import type { HydratedTable, RowGroup, TableMetadata } from "./types";
import { useTableRows } from "./use-table-rows";

interface TableRowsPanelProps {
  table: TableMetadata;
  tableName: string;
  rowGroups: RowGroup[];
  selectedTableIds: ReadonlySet<string>;
  tableNameById: ReadonlyMap<string, string>;
  onIncludeTable: (tableId: Id<"specPilotExtractedTables">) => void;
  onCreateGroup: (args: { name: string; members: GroupMember[]; directions?: string }) => Promise<void>;
  onUpdateGroup: (groupId: string, args: { name?: string; directions?: string }) => Promise<void>;
  onDeleteGroup: (groupId: string) => Promise<void>;
  sourcePdf?: SourcePdfRef | null;
  sourceId: string;
}

type MergeDialogState =
  | { mode: "create"; rowIndices: number[] }
  | { mode: "edit"; group: RowGroup; rowIndices: number[] };

const NO_ROWS: ReadonlySet<number> = new Set();

/** Right column of the job page: the active table's rows, where rows are merged in place. */
export function TableRowsPanel({
  table,
  tableName,
  rowGroups,
  selectedTableIds,
  tableNameById,
  onIncludeTable,
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  sourcePdf,
  sourceId,
}: TableRowsPanelProps) {
  const canEdit = useCanEdit();
  const fullTable = useTableRows(table);
  const [showSource, setShowSource] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  // Merges stay within one table, so opening another table drops the pending selection.
  const [selection, setSelection] = useState<{ tableId: string; rows: ReadonlySet<number> }>({
    tableId: table._id,
    rows: NO_ROWS,
  });
  if (selection.tableId !== table._id) {
    setSelection({ tableId: table._id, rows: NO_ROWS });
  }
  const [mergeDialog, setMergeDialog] = useState<MergeDialogState | null>(null);
  const [unmergeGroup, setUnmergeGroup] = useState<RowGroup | null>(null);
  useOnEditAccessLost(() => {
    setMergeDialog(null);
    setUnmergeGroup(null);
  });

  const tableGroups = useMemo(
    () => rowGroups.filter((group) => group.members.some((member) => member.tableId === table._id)),
    [rowGroups, table._id]
  );
  const layout = useMemo(
    () => (fullTable ? buildRowLayout(table._id, fullTable.rows.length, tableGroups) : []),
    [fullTable, table._id, tableGroups]
  );
  const ungroupedRows = layout.flatMap((item) => (item.kind === "row" ? [item.rowIndex] : []));
  const isIncluded = table.selectedForSubmittal;
  const canSelectRows = canEdit && isIncluded;
  const selectedRows = canSelectRows
    ? new Set([...selection.rows].filter((rowIndex) => ungroupedRows.includes(rowIndex)))
    : NO_ROWS;
  const selectedRowIndices = [...selectedRows].sort((a, b) => a - b);
  const headerChecked =
    selectedRows.size === 0
      ? false
      : selectedRows.size === ungroupedRows.length
        ? true
        : "indeterminate";
  const columnCount = (fullTable?.headers.length ?? 0) + (canEdit ? 1 : 0);

  function setSelectedRows(rows: Iterable<number>) {
    setSelection({ tableId: table._id, rows: new Set(rows) });
  }

  function toggleRow(rowIndex: number, checked: boolean) {
    const next = new Set(selectedRows);
    if (checked) next.add(rowIndex);
    else next.delete(rowIndex);
    setSelectedRows(next);
  }

  async function handleMergeSubmit({ name, directions }: { name: string; directions: string }) {
    if (!mergeDialog) return;
    if (mergeDialog.mode === "edit") {
      await onUpdateGroup(mergeDialog.group._id, { name, directions });
      return;
    }
    // Submit exactly the rows the dialog listed, even if live data changed while it was open.
    await onCreateGroup({
      name,
      directions: directions || undefined,
      members: mergeDialog.rowIndices.map((rowIndex) => ({ tableId: table._id, rowIndex })),
    });
    setSelectedRows([]);
  }

  return (
    <section aria-label={tableName} className="min-w-0 space-y-3 lg:flex lg:min-h-0 lg:flex-col">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 space-y-0.5">
          <h2 className="truncate text-base font-semibold">{tableName}</h2>
          <p className="text-sm text-muted-foreground">
            Page {table.pageNumber} · {table.rowCount} rows × {table.headerCount} columns
          </p>
        </div>
        <Toggle variant="outline" size="sm" pressed={showSource} onPressedChange={setShowSource}>
          <PanelRight />
          Source Page
        </Toggle>
        <Button
          variant="outline"
          size="sm"
          disabled={!fullTable}
          onClick={() => setIsEditorOpen(true)}
        >
          {canEdit ? <Pencil /> : <Eye />}
          {canEdit ? "Edit Table" : "View Table"}
        </Button>
      </div>

      {canEdit ? (
        <SelectionBar
          isIncluded={isIncluded}
          selectedCount={selectedRows.size}
          onInclude={() => onIncludeTable(table._id)}
          onClear={() => setSelectedRows([])}
          onMerge={() => setMergeDialog({ mode: "create", rowIndices: selectedRowIndices })}
        />
      ) : null}

      <div
        className={cn(
          "grid gap-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto",
          showSource
            ? "xl:grid-cols-[minmax(0,1fr)_minmax(360px,40%)] xl:grid-rows-[minmax(0,1fr)]"
            : "lg:grid-rows-[minmax(0,1fr)]"
        )}
      >
        <div className="min-w-0 lg:min-h-0">
          {fullTable === undefined ? (
            <p className="rounded-lg border-2 border-border p-6 text-sm text-muted-foreground">
              Loading table rows...
            </p>
          ) : fullTable === null ? (
            <p className="rounded-lg border-2 border-border p-6 text-sm text-muted-foreground">
              This table's rows could not be loaded.
            </p>
          ) : (
            <Table containerClassName="lg:max-h-full lg:overflow-auto">
              {/* Sticky collapsed-table borders scroll away, so an inset shadow keeps the header divider. */}
              <TableHeader className="lg:sticky lg:top-0 lg:z-10 lg:bg-muted lg:shadow-[inset_0_-2px_0_var(--border)]">
                <TableRow>
                  {canEdit ? (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={headerChecked}
                        disabled={!canSelectRows || ungroupedRows.length === 0}
                        onCheckedChange={() =>
                          setSelectedRows(headerChecked === true ? [] : ungroupedRows)
                        }
                        aria-label="Select all unmerged rows to merge"
                      />
                    </TableHead>
                  ) : null}
                  {fullTable.headers.map((header, index) => (
                    <TableHead key={index}>{header || `Column ${index + 1}`}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {layout.map((item) =>
                  item.kind === "row" ? (
                    <DataRow
                      key={`row-${item.rowIndex}`}
                      table={fullTable}
                      rowIndex={item.rowIndex}
                      showCheckbox={canEdit}
                      selectable={canSelectRows}
                      selected={selectedRows.has(item.rowIndex)}
                      onToggle={(checked) => toggleRow(item.rowIndex, checked)}
                    />
                  ) : (
                    <MergedRows
                      key={`group-${item.group._id}`}
                      table={fullTable}
                      group={item.group}
                      rowIndices={item.rowIndices}
                      columnCount={columnCount}
                      canEdit={canEdit}
                      excludedTableNames={excludedMemberTableIds(item.group, selectedTableIds).map(
                        (tableId) => tableNameById.get(tableId) ?? "Untitled Table"
                      )}
                      onEdit={() =>
                        setMergeDialog({ mode: "edit", group: item.group, rowIndices: item.rowIndices })
                      }
                      onUnmerge={() => setUnmergeGroup(item.group)}
                    />
                  )
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {showSource ? (
          <SpecPilotSourcePdfPanel
            sourcePdf={sourcePdf}
            sourceId={sourceId}
            pageNumber={table.pageNumber}
            className="h-[70dvh] min-h-[360px] xl:h-full xl:min-h-0"
          />
        ) : null}
      </div>

      {/* Mounted only while open, so each opening starts from the rows and name it was opened with. */}
      {mergeDialog && fullTable ? (
        <MergeRowsDialog
          onClose={() => setMergeDialog(null)}
          mode={mergeDialog.mode}
          initialName={mergeDialog.mode === "edit" ? mergeDialog.group.name : tableName}
          initialDirections={mergeDialog.mode === "edit" ? mergeDialog.group.directions : ""}
          rows={mergeDialog.rowIndices.map((rowIndex) => fullTable.rows[rowIndex] ?? [])}
          onSubmit={handleMergeSubmit}
        />
      ) : null}

      {unmergeGroup ? (
        <ConfirmationDialog
          open
          onOpenChange={(open) => !open && setUnmergeGroup(null)}
          title={`Unmerge “${unmergeGroup.name}”?`}
          description="Its rows go back to being separate line items the next time you generate."
          confirmLabel="Unmerge"
          confirmVariant="destructive"
          onConfirm={() => onDeleteGroup(unmergeGroup._id)}
        />
      ) : null}

      {fullTable ? (
        <TableEditorModal
          table={fullTable}
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          sourcePdf={sourcePdf}
          sourceId={sourceId}
        />
      ) : null}
    </section>
  );
}

function SelectionBar({
  isIncluded,
  selectedCount,
  onInclude,
  onClear,
  onMerge,
}: {
  isIncluded: boolean;
  selectedCount: number;
  onInclude: () => void;
  onClear: () => void;
  onMerge: () => void;
}) {
  if (!isIncluded) {
    return (
      <div className="flex min-h-10 flex-wrap items-center gap-3 rounded-md border border-dashed bg-muted/50 px-3 py-1.5 text-sm text-muted-foreground">
        <span className="flex-1">Excluded from the submittal. Its rows won't be generated.</span>
        <Button variant="outline" size="xs" onClick={onInclude}>
          <Plus />
          Include Table
        </Button>
      </div>
    );
  }
  if (selectedCount === 0) {
    return (
      <p className="flex min-h-10 items-center gap-2 px-3 text-sm text-muted-foreground">
        <Layers className="size-4" aria-hidden="true" />
        Check rows to merge them into one submittal line item.
      </p>
    );
  }
  return (
    <div
      role="status"
      className="flex min-h-10 flex-wrap items-center gap-2 rounded-md border border-primary/45 bg-primary/10 py-1 pe-1.5 ps-3 text-sm"
    >
      <span className="flex-1">
        <span className="font-semibold">{selectedCount}</span>{" "}
        {selectedCount === 1 ? "row" : "rows"} selected
      </span>
      <Button variant="ghost" size="xs" onClick={onClear}>
        Clear
      </Button>
      <Button size="xs" onClick={onMerge}>
        <Layers />
        Merge Rows
      </Button>
    </div>
  );
}

/** Short marks ("B-20", "RTU-1") stay on one line; longer first-column text still wraps. */
const MAX_UNWRAPPED_MARK_LENGTH = 16;

function RowCells({ table, rowIndex }: { table: HydratedTable; rowIndex: number }) {
  const row = table.rows[rowIndex] ?? [];
  return table.headers.map((_, columnIndex) => (
    <TableCell
      key={columnIndex}
      className={cn(
        "max-w-[32ch] whitespace-normal break-words px-3 py-2 align-top",
        columnIndex === 0 && "font-medium",
        columnIndex === 0 &&
          (row[0]?.length ?? 0) <= MAX_UNWRAPPED_MARK_LENGTH &&
          "whitespace-nowrap"
      )}
    >
      {row[columnIndex]?.trim() ? (
        row[columnIndex]
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </TableCell>
  ));
}

function DataRow({
  table,
  rowIndex,
  showCheckbox,
  selectable,
  selected,
  onToggle,
}: {
  table: HydratedTable;
  rowIndex: number;
  showCheckbox: boolean;
  selectable: boolean;
  selected: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const label = table.rows[rowIndex]?.[0]?.trim() || `row ${rowIndex + 1}`;
  return (
    <TableRow
      data-state={selected ? "selected" : undefined}
      className={cn(selectable && "cursor-pointer", selected && "bg-primary/10 data-[state=selected]:bg-primary/10")}
      onClick={selectable ? () => onToggle(!selected) : undefined}
    >
      {showCheckbox ? (
        <TableCell className="w-10 py-2 align-top" onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={selected}
            disabled={!selectable}
            onCheckedChange={(checked) => onToggle(checked === true)}
            aria-label={`Select ${label} to merge`}
          />
        </TableCell>
      ) : null}
      <RowCells table={table} rowIndex={rowIndex} />
    </TableRow>
  );
}

function MergedRows({
  table,
  group,
  rowIndices,
  columnCount,
  canEdit,
  excludedTableNames,
  onEdit,
  onUnmerge,
}: {
  table: HydratedTable;
  group: RowGroup;
  rowIndices: number[];
  columnCount: number;
  canEdit: boolean;
  excludedTableNames: string[];
  onEdit: () => void;
  onUnmerge: () => void;
}) {
  const isActive = excludedTableNames.length === 0;
  const otherRowCount = group.members.length - rowIndices.length;
  const accent = isActive ? "shadow-[inset_3px_0_0_var(--primary)]" : "shadow-[inset_3px_0_0_var(--border)]";

  return (
    <>
      <TableRow className={cn("hover:bg-transparent", isActive ? "bg-primary/5" : "bg-muted/60")}>
        <TableCell colSpan={columnCount} className={cn("whitespace-normal px-3 py-1.5", accent)}>
          {/* Actions sit beside the name: wide tables scroll sideways, hiding the row's far end. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Layers
              className={cn("size-4 shrink-0", isActive ? "text-primary" : "text-muted-foreground")}
              aria-hidden="true"
            />
            <span className="font-semibold">{group.name}</span>
            <Badge variant="outline" size="sm">
              {group.members.length} Rows → 1 Line Item
            </Badge>
            {canEdit ? (
              <span className="flex gap-1">
                <Button variant="ghost" size="xs" onClick={onEdit} aria-label={`Edit merge ${group.name}`}>
                  <Pencil />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={onUnmerge}
                  aria-label={`Unmerge ${group.name}`}
                >
                  <Unlink />
                  Unmerge
                </Button>
              </span>
            ) : null}
            {otherRowCount > 0 ? (
              <span className="text-xs text-muted-foreground">
                +{otherRowCount} from other tables
              </span>
            ) : null}
            {isActive ? null : (
              <span className="text-xs font-medium text-status-pending">
                Inactive: include {excludedTableNames.join(", ")} to apply
              </span>
            )}
            {group.directions ? (
              <span className="min-w-0 truncate text-xs italic text-muted-foreground" title={group.directions}>
                {group.directions}
              </span>
            ) : null}
          </div>
        </TableCell>
      </TableRow>
      {rowIndices.map((rowIndex) => (
        <TableRow key={rowIndex} className={cn(!isActive && "text-muted-foreground")}>
          {canEdit ? <TableCell className={cn("w-10 py-2", accent)} /> : null}
          <RowCells table={table} rowIndex={rowIndex} />
        </TableRow>
      ))}
    </>
  );
}
